import { useEffect } from 'react'

import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'

import commerce from '@lib/api/commerce'
import { Layout } from '@components/common'
import { getGlobalContentContext } from '@drf/getGlobalContentContext'
import { useGlobalContentContext } from '@context/GlobalContentContext'
import { PostView } from '@components/blog'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const config = { locale, locales }
  const pagesPromise = commerce.getAllPages({ config, preview })
  const productPromise = commerce.getProduct({
    variables: { slug: 'loreone-1' },
    config,
    preview,
  })

  const blogPagesPromise = commerce.getAllPosts({ config, preview })

  const { pages } = await pagesPromise
  const { product } = await productPromise

  const { posts } = await blogPagesPromise

  const post = posts.find(
    (post) =>
      post.url
        ?.replace('the-logbook/', '')
        .split('/logbook/')[1]
        .split('/')[0] === params?.slug
  )
  const postIndex = posts.findIndex(
    (post) =>
      post.url
        ?.replace('the-logbook/', '')
        .split('/logbook/')[1]
        .split('/')[0] === params?.slug
  )

  if (!post) {
    throw new Error(`Post with url ${params?.slug} not found`)
  }

  if (!product) {
    throw new Error(`Product with slug _loreone-1_ not found`)
  }

  const nextPosts =
    postIndex > 3
      ? posts.slice(0, 3)
      : posts.slice(0, 4).filter((_post) => _post != post)

  const global = await getGlobalContentContext()

  return {
    props: {
      pages,
      global,
      product,
      post,
      posts: nextPosts,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const config = { locales }
  const blogPagesPromise = commerce.getAllPosts({ config })
  const { posts } = await blogPagesPromise

  return {
    paths: locales
      ? locales.reduce<string[]>((arr, locale) => {
          // Add a product path for every locale
          posts.forEach((post: any) => {
            arr.push(`/${locale}${post.url.replace('the-logbook/', '')}`)
          })
          return arr
        }, [])
      : posts.map((post: any) => `${post.url.replace('the-logbook/', '')}`),
    fallback: 'blocking',
  }
}

export default function Post({
  post,
  posts,
  global,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()

  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(global)
  }, [global, setData])

  return router.isFallback ? (
    <h1>Loading...</h1>
  ) : (
    <>
      <PostView data={post} posts={posts} />
    </>
  )
}

Post.Layout = Layout
